import React, { FC, useState, useEffect, Fragment } from 'react';
import { LoadingBlock } from '../LoadingBlock';

export enum ModalSize {
    Small = "modal-sm",
    Medium = '',
    Large = 'modal-lg'
}

export type OnDidConfirmCallback = () => boolean | void | Promise<void> | Promise<boolean>;

export interface CustomModalState {
    /** Whether the modal is currently open, and being rendered. */
    isModalOpen?: boolean

    /** Whether to include a close icon. Will default to true. */
    includeCloseIcon?: boolean,

    /** The title for the modal. If not supplied, no title will be rendered. */
    modalTitle?: string,

    /** Callback for immediately after the user presses the "Confirm" button. Will only proceed to close the modal if the callback returns "true." (or if the callback is not supplied). */
    onDidConfirm?: OnDidConfirmCallback,

    /** Function that fires prior to modal closing. Returning true will allow modal to close - false will prevent closure. */
    onBeforeClose?: (didUserConfirm: boolean) => boolean,

    /** Function that fires just after the modal has closed itself. */
    onDidClose?: (didUserConfirm: boolean) => void,

    __onCleanup?: () => void,

    /** If not supplied, close button will not render.*/
    footerCloseButtonLabel?: string

    /** If not supplied, confirm button will not render.*/
    footerConfirmButtonLabel?: string,

    modalSize?: ModalSize,

    /** An optional message to appear in the modal body if provided. */
    bodyMessage?: string,

    /** Whether the modal should render with an opaque backdrop. Defaults to true.  */
    includeBackdrop?: boolean
}

type Props = CustomModalState;

const CustomModal: FC<Props> = ({ 
    isModalOpen: propsIsModalOpen = false,
    includeCloseIcon = true,
    includeBackdrop = true,
    modalSize = ModalSize.Medium,
    modalTitle, 
    onDidConfirm,
    onBeforeClose,
    onDidClose,
    __onCleanup: onCleanup,
    footerCloseButtonLabel,
    footerConfirmButtonLabel,
    children,
    bodyMessage }) => {

    const [ isModalOpen, setIsModalOpen ] = useState(propsIsModalOpen);
    useEffect(() => setIsModalOpen(propsIsModalOpen), [ propsIsModalOpen ]);

    const [ isAwaitingConfirmCallback, setIsAwaitingConfirmCallback ] = useState(false);

    const onModalConfirmCallback = async () => {
        const takeDefaultAction = () => onModalCloseCallback(true);

        // If no on confirm callback was provided, just take the default action and bail.
        if (!onDidConfirm) {
            takeDefaultAction();
            return;
        }

        setIsAwaitingConfirmCallback(true);

        try {
            // Resolve to a promise, so we can support both async and sync results the same way.
            const callBackResult = await Promise.resolve(onDidConfirm());

            // Only in the case that the callback explicitly returns false do we not progress to close the modal.
            // For both true, and no callback, we will do that.
            if (callBackResult === false) {
                return;
            }
        }
        finally {
            setIsAwaitingConfirmCallback(false);
        }

        // Callback told us to close, or no callback was provided.
        takeDefaultAction();
    };

    const onModalCloseCallback = (didUserConfirm = false) => {
        if (onBeforeClose) {
            const shouldClose = onBeforeClose(didUserConfirm);

            if (!shouldClose) {
                return;
            }
        }

        setIsModalOpen(false);

        if (onDidClose) {
            onDidClose(didUserConfirm);
        }

        if (onCleanup) {
            onCleanup();
        }
    };

    //////////////////// (RENDERING LOGIC STARTS HERE) ///////////////////////////

    // I am unsure if this is a good idea, but going to try this for now. Render absolutely nothing once the modal has been flagged as the final close.
    if (!isModalOpen) {
        return null;
    }

    // There may be more things affecting this status in the future.
    const isModalInLoadingState = isAwaitingConfirmCallback;

    const isModalVisible = isModalOpen;

    const modalClasses = `modal fade${isModalVisible ? ' show' : ''}`;
    const modalStyle = {
        display: isModalVisible ? 'block' : 'none'
    };
    
    const renderModalHeader = () => {
        // If the modal has no title, and no close icon, there is no reason to render a header (at the moment).
        if (!modalTitle && !includeCloseIcon) {
            return null;
        }

        return (
            <div className="modal-header">
                {modalTitle && <h5 className="modal-title">{modalTitle}</h5> }
                {includeCloseIcon && 
                    <button disabled={isModalInLoadingState} type="button" className="close" onClick={() => onModalCloseCallback()} aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                }
            </div>
        );
    };


    const renderModalFooter = () => {
        // Don't render a footer at all if neither button is present.
        if (!footerCloseButtonLabel && !footerConfirmButtonLabel) {
            return null;
        }

        return (
            <div className="modal-footer">
                {footerConfirmButtonLabel && <button disabled={isModalInLoadingState} type="button" className="btn btn-primary" onClick={onModalConfirmCallback}>{footerConfirmButtonLabel}</button>}
                {footerCloseButtonLabel && <button disabled={isModalInLoadingState} type="button" className="btn btn-secondary" onClick={() => onModalCloseCallback()}>{footerCloseButtonLabel}</button>}
            </div>
        );
    };

    const renderModalBackdrop = () => {
        return <div className="modal-backdrop fade show" />;
    };

    return (
        <Fragment>
        <div 
            className={modalClasses} 
            style={modalStyle}
            id="custom-modal"
            tabIndex={-1}
            role="dialog"
            aria-labelledby="exampleModalLabel" 
            aria-hidden={isModalVisible}
        >
            <div
                className={`modal-dialog modal-dialog-centered ${modalSize}`}
                role="document" 
                style={{ maxWidth: "1200px", maxHeight: "calc(90% - (1.75rem * 2))" }}
            >

                <div className="modal-content">
                    {renderModalHeader()}

                    { /* Note: Using CSS to show and hide the children here because we don't want to force the children to re-mount just because of visibility changes.*/}
                    <div className="modal-body">
                        <div style={{display: isModalInLoadingState ? 'none' : 'block'}}>
                            <p>{bodyMessage}</p>
                            {children}
                        </div>
                        { isModalInLoadingState && <LoadingBlock /> }
                    </div>

                    {renderModalFooter()}

                </div>
            </div>
        </div>

        {isModalVisible && includeBackdrop && renderModalBackdrop()}

        </Fragment>
    );
};

export default CustomModal;